/*
    Creation Time: 2021 - Sep - 26
    Created by:  (hamidrezakk)
    Maintainers:
       1.  HamidrezaKK (hamidrezakks@gmail.com)
    Auditor: HamidrezaKK
*/

import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { Typography } from '@qlub-dev/qlub-kit';
import LanguageSwitcher from '../LanguageSwitcher';
import { getLocaleText } from '../MultiLocale';
import { useConfigContext } from '../../context/config';
import { LogoQlub } from '../SVG';
import { interpolateT, useTranslation } from '../../hook/translations';
import { getAvailableLang } from '../../utility/k_lang';
import { useVendor } from '../../context/vendor';
import { useQlubRouter } from '../../hook/router';
import { Error } from '../../repository/common';
import { EnableEnum, IRestaurant } from '../../repository/vendor';

import styles from './index.module.scss';

const languageSwitcherUIv2PathnameList = [
    '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]',
    '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice',
];

function handleVendorConfig({
    vendor,
    error,
    setShowLogo,
}: {
    vendor: IRestaurant | undefined;
    error: Error | undefined;
    setShowLogo: (arg0: boolean) => void;
}) {
    if (!vendor) {
        return;
    }

    if (vendor.config && vendor.config.hideLogo) {
        setShowLogo(!vendor.config.hideLogo);
    }

    if (error) {
        setShowLogo(true);
    }
}

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    fixed?: boolean;
    gray?: boolean;
    opaque?: boolean;
}

// eslint-disable-next-line react/function-component-definition
const Footer: FC<IProps> = ({ fixed, gray, opaque, ...props }) => {
    const { t } = useTranslation('common');
    const [showLogo, setShowLogo] = useState(true);
    const { url, pathname, lang, query } = useQlubRouter();
    const { vendor, error } = useVendor();
    const { config: countryConfig } = useConfigContext();

    const isLanguageSwitcherUIv2 =
        languageSwitcherUIv2PathnameList.includes(pathname) &&
        countryConfig?.languageSwitcherUIv2 === EnableEnum.Enable;

    const landingFooterDisclaimerText = useMemo(() => {
        if (pathname !== '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]') {
            return '';
        }

        const disclaimerText =
            getLocaleText(vendor?.config?.landingFooterDisclaimerText || '', lang || '', 'en') ||
            getLocaleText(countryConfig?.landingFooterDisclaimerText || '', lang || '', 'en');

        return disclaimerText;
    }, [vendor?.config?.landingFooterDisclaimerText, countryConfig?.landingFooterDisclaimerText, lang, pathname]);

    useEffect(() => {
        handleVendorConfig({ vendor, error, setShowLogo });
    }, [vendor, error]);

    if (pathname.indexOf('/menu/') === 0) {
        return null;
    }

    const getLinkWrapper = (child: ReactNode) => {
        return (
            <Typography component="span" secondColor className={styles.purple}>
                <Link
                    data-qa-id="terms-and-conditions"
                    href={{
                        pathname: '/qr/[cc]/terms',
                        query: {
                            ...query,
                            cc: url.cc || vendor?.country.code || '',
                            ...getAvailableLang(lang),
                        },
                    }}
                >
                    {child}
                </Link>
            </Typography>
        );
    };

    const renderTermAndConditions = () => {
        const text =
            url.cc === 'sg' && (lang || 'en') === 'en'
                ? t('Terms and Conditions & Privacy Policy')
                : t('Terms and Conditions');

        return interpolateT(t('By using Qlub_ you accept our <terms>'), {
            terms: getLinkWrapper(text),
        });
    };

    return (
        <div
            {...props}
            className={classNames(
                styles.footer,
                {
                    [styles.fixed]: fixed,
                    [styles.transparentBg]: vendor?.backgroundUrl && !opaque,
                    [styles.grayBg]: gray,
                },
                props.className,
            )}
        >
            <div className={styles.logo} title={t('Qlub_')}>
                {!isLanguageSwitcherUIv2 && <LanguageSwitcher languageSwitcherV2={false} />}
                {showLogo && <LogoQlub height={isLanguageSwitcherUIv2 ? '18.68px' : '14px'} />}
            </div>

            {landingFooterDisclaimerText && <div className={styles.disclaimerText}>{landingFooterDisclaimerText}</div>}
            {pathname !== '/qr/[cc]/terms' && (
                <div
                    className={classNames(styles.terms, {
                        [styles.termsPaddingTopWithDisclaimerText]: landingFooterDisclaimerText,
                    })}
                >
                    {renderTermAndConditions()}
                </div>
            )}
        </div>
    );
};

export default Footer;
